import React, { useState, useEffect } from "react";
import { getICICIMappings, updateICICIMapping } from "../lib/api";
import { ArrowRight, Check, AlertTriangle, HelpCircle, Loader2, CheckCircle2, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const STATUS_CONFIG = {
  confirmed: { color: "text-[var(--positive)]", icon: CheckCircle2, label: "Confirmed", bg: "bg-emerald-500/10 border-emerald-500/20" },
  exact: { color: "text-[var(--positive)]", icon: Check, label: "Exact Match", bg: "bg-emerald-500/10 border-emerald-500/20" },
  auto: { color: "text-[var(--brand)]", icon: Check, label: "Auto-mapped", bg: "bg-blue-500/10 border-blue-500/20" },
  guessed: { color: "text-[var(--warning)]", icon: AlertTriangle, label: "Guessed", bg: "bg-amber-500/10 border-amber-500/20" },
  unknown: { color: "text-[var(--negative)]", icon: HelpCircle, label: "Unknown", bg: "bg-red-500/10 border-red-500/20" },
};

export default function MappingsPage() {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCode, setEditCode] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all"); // all, review, confirmed

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await getICICIMappings();
      setMappings(data.mappings || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async (iciciCode) => {
    if (!editValue.trim()) return;
    setSaving(true);
    try {
      await updateICICIMapping({ icici_code: iciciCode, nse_symbol: editValue.trim().toUpperCase() });
      setEditCode(null);
      setEditValue("");
      load();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const filtered = mappings.filter(m => {
    if (filter === "review") return m.status === "guessed" || m.status === "unknown";
    if (filter === "confirmed") return m.status === "confirmed" || m.status === "exact";
    return true;
  });

  const needsReview = mappings.filter(m => m.status === "guessed" || m.status === "unknown").length;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[var(--brand)]" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-6" data-testid="mappings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>ICICI Symbol Mappings</h1>
          <p className="text-sm text-[var(--text-muted)]">
            Review and correct how ICICI stock codes map to NSE symbols.
            {needsReview > 0 && <span className="text-[var(--warning)]"> {needsReview} need review.</span>}
          </p>
        </div>
        <div className="flex gap-1 bg-[var(--bg-surface)] border border-white/10 rounded-md p-0.5">
          {[["all", `All (${mappings.length})`], ["review", `Needs Review (${needsReview})`], ["confirmed", "Confirmed"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${filter === k ? "bg-[var(--brand)] text-white" : "text-[var(--text-secondary)] hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Info Banner */}
      {needsReview > 0 && filter === "all" && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-md p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-[var(--warning)] mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-[var(--warning)]">{needsReview} mappings need your review</div>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              ICICI uses abbreviated stock codes. Some have been auto-guessed and may be wrong.
              Click the NSE symbol to correct it. Your corrections are saved permanently.
            </p>
          </div>
        </div>
      )}

      {/* Mappings Table */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden" data-testid="mappings-table">
        <div className="divide-y divide-white/5">
          <div className="grid grid-cols-14 gap-4 px-5 py-3 text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold border-b border-white/10" style={{gridTemplateColumns: "repeat(14, minmax(0, 1fr))"}}>
            <div className="col-span-3">ICICI Code</div>
            <div className="col-span-1 text-center"></div>
            <div className="col-span-3">NSE Symbol</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">Avg / CMP</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Action</div>
          </div>

          {filtered.map((m) => {
            const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.unknown;
            const Icon = cfg.icon;
            const isEditing = editCode === m.icici_code;

            return (
              <div key={m.icici_code}
                className={`grid grid-cols-14 gap-4 px-5 py-3 items-center hover:bg-white/[0.02] transition-colors ${
                  m.status === "guessed" || m.status === "unknown" ? "bg-amber-500/[0.02]" : ""
                }`}
                style={{gridTemplateColumns: "repeat(14, minmax(0, 1fr))"}}
                data-testid={`mapping-${m.icici_code}`}
              >
                <div className="col-span-3">
                  <span className="font-mono text-sm font-medium text-white">{m.icici_code}</span>
                </div>

                <div className="col-span-1 flex justify-center">
                  <ArrowRight size={14} className="text-[var(--text-muted)]" />
                </div>

                <div className="col-span-3">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value.toUpperCase())}
                        placeholder="NSE symbol"
                        className="bg-[var(--bg-base)] border-[var(--brand)] text-white font-mono text-sm h-8"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSave(m.icici_code);
                          if (e.key === "Escape") { setEditCode(null); setEditValue(""); }
                        }}
                        data-testid={`edit-mapping-${m.icici_code}`}
                      />
                      <button
                        onClick={() => handleSave(m.icici_code)}
                        disabled={saving}
                        className="p-1.5 bg-[var(--brand)] rounded text-white hover:bg-[var(--brand-hover)]"
                      >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      </button>
                    </div>
                  ) : (
                    <span
                      className={`font-mono text-sm cursor-pointer hover:underline ${
                        m.status === "guessed" || m.status === "unknown" ? "text-[var(--warning)]" : "text-white"
                      }`}
                      onClick={() => { setEditCode(m.icici_code); setEditValue(m.nse_symbol); }}
                      title="Click to edit"
                    >
                      {m.nse_symbol}
                    </span>
                  )}
                </div>

                <div className="col-span-1 text-right text-xs font-mono text-[var(--text-secondary)]">
                  {m.quantity ? m.quantity.toLocaleString("en-IN") : "—"}
                </div>

                <div className="col-span-2 text-right text-[11px] font-mono">
                  {m.avg_price ? (
                    <>
                      <div className="text-[var(--text-muted)]">₹{Number(m.avg_price).toFixed(1)}</div>
                      <div className="text-white">₹{Number(m.last_price).toFixed(1)}</div>
                    </>
                  ) : "—"}
                </div>

                <div className="col-span-2">
                  <Badge variant="outline" className={`text-[10px] ${cfg.bg} ${cfg.color} border`}>
                    <Icon size={10} className="mr-1" />
                    {cfg.label}
                  </Badge>
                </div>

                <div className="col-span-2 text-right">
                  {!isEditing && (
                    <button
                      onClick={() => { setEditCode(m.icici_code); setEditValue(m.nse_symbol); }}
                      className="text-xs text-[var(--text-muted)] hover:text-[var(--brand)] transition-colors"
                      data-testid={`edit-btn-${m.icici_code}`}
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
